import axios from 'axios';
import moment from 'moment';
import { TFunction } from 'i18next';
import appState from '../../state/AppStateContainer';
import { BatchOutcome, RecipientInput, SendOutcome } from '../../services/service-evaluations';
import { EvaluationErrors, EvaluationLimits } from '../../util/evaluationContract';
import { toIsoDay } from '../../util/bidRequestReminder';
import { buildNameList } from '../../util/supplierBulkOutcome';

export type RecipientOption = {
  value: string;
  label: string;
  _id?: string;
  name?: string;
  email?: string;
  __isNew__?: boolean;
};

export type OutcomeToast = { kind: 'success' | 'info' | 'warning' | 'error'; message: string };

type RoleUser = { _id: string; name: string; email: string };
type RoleGroup = { users: RoleUser[] };

const emailOf = (option: { email?: string | null }) => (option.email ?? '').toLowerCase();

// Mirrors SINGLE_ADDRESS in src/services/evaluationBatchService.js, which silently drops any other address.
const SINGLE_ADDRESS = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;
const SEPARATOR = /[\s,;]/;
const SEPARATORS = /[\s,;]+/;

export const toRecipientInputs = (options: RecipientOption[]): RecipientInput[] =>
  options.map(option => ({
    ...(option._id && { userId: option._id }),
    ...(option.email && { email: option.email }),
    ...(option.name && { name: option.name }),
  }));

export const isValidEmail = (email: string) => {
  const trimmed = email.trim();
  return trimmed.length <= EvaluationLimits.EmailMaxLength && SINGLE_ADDRESS.test(trimmed);
};

export const splitPastedEmails = (text: string): string[] =>
  text
    .split(/[,;\r\n]+/)
    .flatMap(entry => {
      const bracketed = entry.match(/<([^<>]+)>/);
      return bracketed ? [bracketed[1]] : entry.split(SEPARATORS);
    })
    .map(token => token.trim())
    .filter(Boolean);

export const splitTypedEmails = (text: string): { complete: string[]; rest: string } => {
  if (!SEPARATOR.test(text)) return { complete: [], rest: text };
  const parts = text.split(SEPARATORS);
  const rest = SEPARATOR.test(text.slice(-1)) ? '' : parts.pop() ?? '';
  const complete = parts.filter(Boolean);
  const isNameSearch = !/[,;]/.test(text) && !complete.every(part => part.includes('@'));
  if (!complete.length || isNameSearch) return { complete: [], rest: text };
  return { complete, rest };
};

export const isOwnRecipientOption = (option: { _id?: string | null; email?: string | null }) => {
  const userId = appState.getUserId();
  const email = (appState.getUserEmail() ?? '').toLowerCase();
  return Boolean((userId && option._id === userId) || (email && emailOf(option) === email));
};

// The label is display only: toRecipientInputs never sends it.
export const userRecipientOptions = (groups: RoleGroup[], t: TFunction): RecipientOption[] => {
  const users = groups.flatMap(group => group.users);
  const idsByName = new Map<string, Set<string>>();
  users.forEach(user => {
    const ids = idsByName.get(user.name) ?? new Set<string>();
    idsByName.set(user.name, ids.add(user._id));
  });
  return users.map(user => {
    const shared = (idsByName.get(user.name)?.size ?? 0) > 1;
    const option: RecipientOption = {
      ...user,
      label: shared && user.email ? `${user.name} (${user.email})` : user.name,
      value: user._id,
    };
    return isOwnRecipientOption(option)
      ? { ...option, label: t('recipients.ownLabel', '{{name}} (du)', { name: option.label }) }
      : option;
  });
};

export const typedRecipientOption = (email: string): RecipientOption => ({
  value: email,
  label: email,
  email,
  __isNew__: true,
});

export const recipientOptionFor = (email: string, users: RecipientOption[]): RecipientOption =>
  users.find(option => emailOf(option) === email.toLowerCase()) ?? typedRecipientOption(email);

const isSameRecipient = (a: RecipientOption, b: RecipientOption) =>
  Boolean((a._id && a._id === b._id) || (emailOf(a) && emailOf(a) === emailOf(b)));

export const mergeRecipients = (
  current: readonly RecipientOption[],
  typed: string[],
  users: RecipientOption[],
  max: number,
): { next: RecipientOption[]; invalid: string[] } => {
  const next: RecipientOption[] = [];
  const invalid: string[] = [];
  const add = (option: RecipientOption) => {
    if (next.length >= max || next.some(kept => isSameRecipient(kept, option))) return;
    next.push(option);
  };
  current.forEach(add);
  typed.forEach(token => {
    const email = token.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      if (!invalid.includes(email)) invalid.push(email);
      return;
    }
    add(recipientOptionFor(email, users));
  });
  return { next, invalid };
};

// Must match the failures services/axiosHelper.js already toasts, or the user gets two notices or none.
export const hasGlobalNotice = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return (
    error.message === 'Network Error' ||
    status === 401 ||
    status === 415 ||
    (status === 403 && !appState.isSupplierRole())
  );
};

const ERROR_TEXT: Record<string, [string, string]> = {
  [EvaluationErrors.Closed]: ['errors.closed', 'Utvärderingen är stängd.'],
  [EvaluationErrors.AlreadyAnswered]: ['errors.alreadyAnswered', 'Mottagaren har redan svarat.'],
  [EvaluationErrors.DuplicateRecipient]: [
    'errors.duplicateRecipient',
    'Personen är redan mottagare.',
  ],
  [EvaluationErrors.RecipientIsSupplierContact]: [
    'errors.recipientIsSupplierContact',
    'Leverantörens egna kontaktpersoner kan inte utvärdera leverantören.',
  ],
  [EvaluationErrors.InvalidEmail]: ['errors.invalidEmail', 'Ogiltig e-postadress.'],
  [EvaluationErrors.NoRecipients]: ['errors.noRecipients', 'Lägg till minst en mottagare.'],
  [EvaluationErrors.TooManyRecipients]: [
    'errors.tooManyRecipients',
    'Högst 50 mottagare per utvärdering.',
  ],
  [EvaluationErrors.InvalidContext]: [
    'errors.invalidContext',
    'Kontrollera projekt och sista svarsdag.',
  ],
  [EvaluationErrors.SelfRecipient]: [
    'errors.selfRecipient',
    'Du får inget mejl för din egen rad. Svara med Svara nu.',
  ],
  [EvaluationErrors.NotFound]: [
    'errors.notFound',
    'Utvärderingen eller mottagaren finns inte längre.',
  ],
  [EvaluationErrors.MigrationPending]: [
    'errors.migrationPending',
    'Utvärderingar förbereds. Försök igen om en stund.',
  ],
  [EvaluationErrors.RateLimited]: [
    'errors.rateLimited',
    'Ett mejl skickades nyss. Vänta några minuter innan du skickar igen.',
  ],
  [EvaluationErrors.TooManyTags]: ['errors.tooManyTags', 'Högst 5 taggar per utvärdering.'],
  [EvaluationErrors.TagNameTaken]: [
    'errors.tagNameTaken',
    'Det finns redan en tagg med det namnet.',
  ],
  [EvaluationErrors.TagNameInvalid]: [
    'errors.tagNameInvalid',
    'Taggnamnet måste vara 1–40 tecken.',
  ],
  [EvaluationErrors.TagArchived]: ['errors.tagArchived', 'Taggen är arkiverad.'],
  [EvaluationErrors.TagNotFound]: ['errors.tagNotFound', 'Taggen finns inte längre.'],
  [EvaluationErrors.InvalidQuestions]: [
    'errors.invalidQuestions',
    'En eller flera frågor finns inte längre. Välj frågorna igen.',
  ],
  [EvaluationErrors.BatchInProgress]: [
    'errors.batchInProgress',
    'Utvärderingen skickas redan. Vänta en stund.',
  ],
  [EvaluationErrors.RequestNotClosed]: ['errors.alreadyOpen', 'Utvärderingen är redan öppen.'],
  [EvaluationErrors.SupplierNotEvaluable]: [
    'errors.supplierNotEvaluable',
    'Leverantören kan inte utvärderas.',
  ],
  [EvaluationErrors.TemplateNotFound]: ['errors.templateNotFound', 'Mallen finns inte längre.'],
  [EvaluationErrors.TooManySuppliers]: [
    'errors.tooManySuppliers',
    'Högst 10 leverantörer per utskick.',
  ],
  [EvaluationErrors.TooManyQuestions]: [
    'errors.tooManyQuestions',
    'Högst 40 frågor per utvärdering.',
  ],
};

export const errorKeyOf = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const key = (error.response?.data as { error?: unknown } | undefined)?.error;
  return typeof key === 'string' ? key : undefined;
};

export const evaluationErrorMessage = (t: TFunction, error: unknown): string | null => {
  if (hasGlobalNotice(error)) return null;
  const key = errorKeyOf(error);
  const entry =
    key && Object.prototype.hasOwnProperty.call(ERROR_TEXT, key) ? ERROR_TEXT[key] : undefined;
  const [textKey, fallback] = entry ?? ['errors.generic', 'Något gick fel. Försök igen.'];
  return t(textKey, fallback);
};

type Skipped = { email: string | null; reason: string };

const SKIP_REASON: Record<string, [string, string]> = {
  [EvaluationErrors.DuplicateRecipient]: [
    'toast.skipReason.duplicateRecipient',
    'redan tillfrågad',
  ],
  [EvaluationErrors.RecipientIsSupplierContact]: [
    'toast.skipReason.recipientIsSupplierContact',
    'är kontakt hos leverantören',
  ],
  [EvaluationErrors.RateLimited]: ['toast.skipReason.rateLimited', 'mejlades nyss'],
};

const MAX_NAMED_SKIPS = 5;

const skippedLine = (t: TFunction, skipped: Skipped[]) => {
  const label = (entry: Skipped) => {
    if (!entry.email) return undefined;
    const reason = Object.prototype.hasOwnProperty.call(SKIP_REASON, entry.reason)
      ? SKIP_REASON[entry.reason]
      : undefined;
    return reason ? `${entry.email} (${t(reason[0], reason[1])})` : entry.email;
  };
  const shown = skipped
    .map(label)
    .filter((name): name is string => Boolean(name))
    .slice(0, MAX_NAMED_SKIPS);
  const more = skipped.length - shown.length;
  if (!shown.length) {
    return t('toast.recipientsSkipped', {
      count: skipped.length,
      defaultValue_one: '{{count}} mottagare hoppades över',
      defaultValue_other: '{{count}} mottagare hoppades över',
    });
  }
  const names = more
    ? `${shown.join(', ')} ${t('toast.andMore', 'och {{more}} till', { more })}`
    : shown.join(', ');
  return t('toast.skippedNamed', {
    count: skipped.length,
    names,
    defaultValue_one: '{{count}} mottagare hoppades över: {{names}}',
    defaultValue_other: '{{count}} mottagare hoppades över: {{names}}',
  });
};

const joinSentences = (parts: string[]) =>
  parts.reduce(
    (text, part) => (!text ? part : `${text}${text.endsWith('.') ? ' ' : '. '}${part}`),
    '',
  );

const isBatchOutcome = (outcome: BatchOutcome | SendOutcome): outcome is BatchOutcome =>
  Array.isArray((outcome as BatchOutcome).requestIds);

export type OutcomeMail = 'invitation' | 'dueDateNotice';

const sentLine = (t: TFunction, mail: OutcomeMail, count: number) =>
  mail === 'dueDateNotice'
    ? t('status.changeDueDate.notified', {
        count,
        defaultValue_one: '{{count}} mottagare fick ett mejl om den nya sista svarsdagen',
        defaultValue_other: '{{count}} mottagare fick ett mejl om den nya sista svarsdagen',
      })
    : t('toast.sentToRecipients', {
        count,
        defaultValue_one: 'Utvärderingen skickades till {{count}} mottagare',
        defaultValue_other: 'Utvärderingen skickades till {{count}} mottagare',
      });

const createdNoEmailLine = (t: TFunction, created: number, counted: boolean) =>
  counted
    ? t('wizard.outcome.createdNoEmail', {
        count: created,
        defaultValue_one: '{{count}} utvärdering skapades. Inget mejl skickades.',
        defaultValue_other: '{{count}} utvärderingar skapades. Inget mejl skickades.',
      })
    : t('toast.createdNoEmail', 'Utvärderingen är skapad. Inget mejl skickades.');

const batchSentLine = (t: TFunction, created: number, sent: number) =>
  t('wizard.outcome.created', {
    count: created,
    mails: sent,
    defaultValue_one: '{{count}} utvärdering skickades ({{mails}} mejl).',
    defaultValue_other: '{{count}} utvärderingar skickades ({{mails}} mejl).',
  });

const notEvaluableLine = (
  t: TFunction,
  supplierIds: (string | null)[],
  supplierNames?: Record<string, string>,
) => {
  const { shown, more } = buildNameList(
    supplierIds.map(id => id ?? ''),
    id => supplierNames?.[id],
    MAX_NAMED_SKIPS,
  );
  if (!shown.length) return t('toast.notEvaluable', 'Leverantören kan inte utvärderas');
  const names = more
    ? `${shown.join(', ')} ${t('toast.andMore', 'och {{more}} till', { more })}`
    : shown.join(', ');
  return t('wizard.outcome.notEvaluable', '{{names}} kunde inte utvärderas.', { names });
};

// supplierNames (a wizard send) counts the utvärderingar and names the suppliers that were skipped.
export const outcomeToast = (
  t: TFunction,
  outcome: BatchOutcome | SendOutcome,
  mail: OutcomeMail = 'invitation',
  supplierNames?: Record<string, string>,
): OutcomeToast => {
  if (isBatchOutcome(outcome) && outcome.inProgress) {
    return outcome.requestIds.length
      ? {
          kind: 'info',
          message: t(
            'toast.inProgress',
            'Utvärderingarna skickas fortfarande. Se status om en stund.',
          ),
        }
      : {
          kind: 'warning',
          message: t('errors.batchInProgress', 'Utvärderingen skickas redan. Vänta en stund.'),
        };
  }
  const { sent } = outcome;
  const failed = outcome.failed.length;
  const notEvaluable = outcome.skipped
    .filter(entry => entry.reason === EvaluationErrors.SupplierNotEvaluable)
    .map(entry => (entry as { supplierId?: string | null }).supplierId ?? null);
  const recipientSkips: Skipped[] = outcome.skipped.filter(
    entry => entry.reason !== EvaluationErrors.SupplierNotEvaluable,
  );
  const created = isBatchOutcome(outcome) ? outcome.requestIds.length : null;
  const counted = Boolean(supplierNames) && created !== null;
  const lead =
    created === 0
      ? t('toast.nothingCreated', 'Ingen utvärdering skapades')
      : created && sent === 0 && failed === 0
      ? createdNoEmailLine(t, created, counted)
      : null;
  const parts = [
    lead,
    sent > 0 && (counted && created ? batchSentLine(t, created, sent) : sentLine(t, mail, sent)),
    failed > 0 &&
      t('toast.emailsFailed', {
        count: failed,
        defaultValue_one: '{{count}} mejl kunde inte skickas',
        defaultValue_other: '{{count}} mejl kunde inte skickas',
      }),
    notEvaluable.length > 0 && notEvaluableLine(t, notEvaluable, supplierNames),
    recipientSkips.length > 0 && skippedLine(t, recipientSkips),
  ].filter((part): part is string => Boolean(part));
  if (!parts.length)
    return { kind: 'success', message: t('toast.noEmail', 'Inget mejl skickades.') };
  const held = failed + outcome.skipped.length;
  const kind =
    created === 0
      ? failed > 0
        ? 'error'
        : 'warning'
      : failed > 0 && sent === 0
      ? 'error'
      : held === 0
      ? 'success'
      : 'warning';
  return { kind, message: joinSentences(parts) };
};

export const defaultDueDay = (today: Date): string =>
  toIsoDay(moment(today).add(14, 'days').toDate());

export const defaultTitle = (t: TFunction, supplierName: string, projectName: string | null) =>
  (projectName?.trim()
    ? t('quick.defaultTitle', 'Utvärdering av {{supplier}} – {{project}}', {
        supplier: supplierName,
        project: projectName.trim(),
      })
    : t('quick.defaultTitleNoProject', 'Utvärdering av {{supplier}}', { supplier: supplierName })
  ).slice(0, EvaluationLimits.TitleMaxLength);

export const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export type IdempotencyKey = { key: string; payload: string };

// A retry of the same payload reuses the key so the server replays it instead of sending twice.
export const idempotencyKeyFor = (
  previous: IdempotencyKey | null,
  payload: string,
): IdempotencyKey =>
  previous?.payload === payload ? previous : { key: newIdempotencyKey(), payload };

// No response, a gateway error or batchInProgress: the server may still be sending under the key.
export const isUnansweredSend = (error: unknown) =>
  axios.isAxiosError(error) &&
  (!error.response ||
    [502, 503, 504].includes(error.response.status) ||
    errorKeyOf(error) === EvaluationErrors.BatchInProgress);
